import { createApp } from 'vue';
import axios from 'axios';
import router from './router.js';
import store from './store/index.js';
import { createPinia } from 'pinia';
import { useErrorReportingService } from './services/errorReportingService';
import { initVisitTracking } from './services/visitTracker';
import { initializePerformanceEnhancements, fixThirdPartyPerformanceIssues } from './utils/performanceEnhancements';
import '../css/app.css';
import SpinnerComponent from './components/common/SpinnerComponent.vue'; // Import the spinner component
import FeedbackTab from './components/chat/feedback/FeedbackTab.vue'; // Import the feedback tab component
import App from './App.vue'; // Import the main App component

// Removed PrimeVue imports - migrated to custom UI components
import Notifications from '@kyvg/vue3-notification';

// Other imports
import VueGtag from 'vue-gtag';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import VueMask from '@devindex/vue-mask';
import GuardianAngelQuoteForm from './components/forms/GuardianAngelQuoteForm.vue';

// Import Iconify
import { Icon as IconifyIcon } from '@iconify/vue';

// Chat widget override styles to hide default Chatwoot
import '../css/chat-override.css';

// Set up Pusher
window.Pusher = Pusher;

// Configure Laravel Echo - TEMPORARILY DISABLED
// TODO: Re-enable when WebSocket server (Soketi) is properly configured
/*
window.Echo = new Echo({
    broadcaster: 'pusher',
    key: import.meta.env.VITE_PUSHER_APP_KEY,
    wsHost: getWebSocketHost(),
    wsPort: import.meta.env.VITE_PUSHER_PORT,
    wssPort: import.meta.env.VITE_PUSHER_PORT, // Use the same port for wss if TLS is on VITE_PUSHER_HOST
    forceTLS: (import.meta.env.VITE_PUSHER_SCHEME === 'https'),
    enabledTransports: ['ws', 'wss'],
    cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER, // Cluster is not typically used when self-hosting with Soketi
    // encrypted: true, // This is handled by forceTLS based on scheme
});
*/

// Define these functions globally first, before any other code
window.hideChatwootWidget = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggleBubbleVisibility(false);
  }
};

window.showChatwootWidget = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggleBubbleVisibility(true);
  }
};

function setupChatwoot(callback) {
  const script = document.createElement('script');
  script.src = 'https://insuranceexpresschat.com/packs/js/sdk.js';
  script.async = true;
  script.onload = () => {
    window.chatwootSDK.run({
      // Using the correct working website token
      websiteToken: '5MUh9hiKzyW9fE3MtxJHnzh9',
      baseUrl: 'https://insuranceexpresschat.com',
      hideMessageBubble: true,
      position: 'right',
      locale: 'en',
      useBrowserLanguage: false
    });
    
    // Hide the default Chatwoot widget immediately after initialization
    setTimeout(() => {
      // Hide the default Chatwoot bubble completely
      if (window.$chatwoot) {
        window.$chatwoot.toggleBubbleVisibility(false);
      }
      
      // CSS hiding styles are now in app.css for CSP compliance
      // No need for dynamic style injection
      
      const currentRoute = router.currentRoute.value;
      if (currentRoute.meta.hideChatwoot) {
        window.hideChatwootWidget();
      }
      
      callback();
    }, 1000);
  };
  
  script.onerror = function(error) {
    console.error('🔴 app.js: Failed to load Chatwoot SDK:', error);
  };
  
  document.head.appendChild(script);
}

// Create Vue application
const app = createApp(App);

// Create Pinia instance
const pinia = createPinia();

// Configure Axios
axios.defaults.baseURL =
  import.meta.env.VITE_APP_URL || 'https://insuranceexpress.com'; // Use your actual API URL
axios.defaults.withCredentials = true; // This is crucial for sending cookies

// Initialize error reporting service
const { logFormError, setupGlobalErrorHandlers, showUserErrorMessage, setupVueErrorHandler } = useErrorReportingService();

// Configure global error handler using our enhanced error reporting service
setupVueErrorHandler(app);

// Add a response interceptor
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error(`Axios error: ${error.response?.status} - ${error.message}`);
    
    // Log axios errors to our error reporting service
    logFormError('axios-error', error, {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.config?.data
    });
    
    // Show user-friendly error message for API failures
    if (error.response?.status >= 500) {
      showUserErrorMessage('We\'re experiencing server issues. Please try again later or contact support.', {
        contactEmail: 'policychanges@insuranceexpress.com',
        severity: 'error'
      });
    } else if (error.response?.status === 404) {
      showUserErrorMessage('The requested resource was not found. Please check your URL and try again.', {
        severity: 'warning',
        duration: 5000
      });
    } else if (error.response?.status === 422) {
      // Validation errors - usually handled by form components
      console.log('Validation error:', error.response.data);
    } else {
      showUserErrorMessage('An error occurred while communicating with our server. Please try again.', {
        contactEmail: 'policychanges@insuranceexpress.com',
        severity: 'error'
      });
    }
    
    // No need to handle 401 errors since auth is removed
    return Promise.reject(error);
  },
);

// Register global components and plugins
app.use(router);
app.use(store);
app.use(pinia);
app.use(Notifications);
app.use(VueGtag, { config: { id: 'G-B9F0111JPC' } }, router);
app.use(VueMask, {
  placeholders: {
    ' ': /\s/, // Define space as a placeholder
  },
});

// Register Iconify component globally
app.component('Icon', IconifyIcon);

// Register SpinnerComponent globally
app.component('SpinnerComponent', SpinnerComponent);

// Register FeedbackTab component globally
app.component('FeedbackTab', FeedbackTab);

// Register Axios globally
app.config.globalProperties.$axios = axios;

// Register components
app.component('guardian-angel-quote-form', GuardianAngelQuoteForm);

// Setup global error handlers
setupGlobalErrorHandlers();

// Initialize performance enhancements for better user experience
initializePerformanceEnhancements();
fixThirdPartyPerformanceIssues();

// Mount the app after Chatwoot is fully setup
setupChatwoot(() => {
  // CSS for hiding Chatwoot widget is now in app.css for CSP compliance
  // No need for dynamic style injection
  
  setTimeout(() => {
    // ONLY MOUNT IF #app ELEMENT EXISTS - Prevents conflicts with other Vue apps
    const appElement = document.getElementById('app');
    if (appElement) {
      app.mount('#app');
      // Signal that Vue is initialized
      window.__VUE_APP_INITIALIZED__ = true;
      // Dispatch event for good measure
      window.dispatchEvent(new Event('vueInitialized'));
      
      // Initialize visit tracking for business intelligence
      initVisitTracking();
    } else {
      console.log('ℹ️ No #app element found - skipping main Vue app initialization (likely on specialized page)');
    }
  }, 100);
});
